import { useEffect, useReducer, useRef, useState } from "react";
import { initialNarrativeState, narrativeReducer } from "@/lib/landingNarrative";

const motionQuery = "(prefers-reduced-motion: reduce)";
export function useLandingNarrative() {
  const viewportRef = useRef(null);
  const [state, dispatch] = useReducer(narrativeReducer, initialNarrativeState);
  const [inView, setInView] = useState(false);
  const [visible, setVisible] = useState(() => !document.hidden);
  const [staticMode, setStaticMode] = useState(() => !window.IntersectionObserver || !window.matchMedia || window.matchMedia(motionQuery).matches);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia(motionQuery);
    const update = () => setStaticMode(media.matches || !window.IntersectionObserver);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const update = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    if (staticMode || !viewportRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio >= 0.1), { threshold: 0.1 });
    observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [staticMode]);

  const running = !staticMode && inView && visible && state.playing;
  useEffect(() => {
    if (!running) return undefined;
    let last = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      if (!document.hidden) dispatch({ type: "tick", delta: now - last });
      last = now;
    }, 100);
    return () => window.clearInterval(timer);
  }, [running]);

  return { state, dispatch, staticMode, running, viewportRef };
}
